import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useCurrentAppUser } from './useCurrentAppUser';

// VAPID public key - hardcoded for now, should match VAPID_PUBLIC_KEY in edge function
// This is the PUBLIC key so it's safe to include in client code
const VAPID_PUBLIC_KEY = 'BMfysKqX8aoypkDKXeY5Lla3ohaMN9vOgvGIr8URbLDiDYvObxVSkGBKhdS9tCXlclOAP3AcWjuM9gF3GU42P40';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export interface PushNotificationState {
  isSupported: boolean;
  isSubscribed: boolean;
  permission: NotificationPermission;
  isLoading: boolean;
  error: string | null;
}

export function usePushNotifications() {
  const { user } = useAuth();
  const { data: appUser } = useCurrentAppUser();
  const [state, setState] = useState<PushNotificationState>({
    isSupported: false,
    isSubscribed: false,
    permission: 'default',
    isLoading: false,
    error: null,
  });

  // Check browser support - more robust for iOS PWA
  useEffect(() => {
    const checkSupport = async () => {
      const hasSW = 'serviceWorker' in navigator;
      const hasPM = 'PushManager' in window;
      const hasNotif = 'Notification' in window;
      const isSupported = hasSW && hasPM && hasNotif;
      const permission = hasNotif ? Notification.permission : 'denied';
      
      console.log('[Push Debug] Support check:', { 
        serviceWorker: hasSW, 
        PushManager: hasPM, 
        Notification: hasNotif, 
        isSupported, 
        permission,
        appUser: appUser?.id 
      });
      
      setState(prev => ({ ...prev, isSupported, permission }));
      
      // Check if already subscribed - don't require controller (iOS PWA may not have it immediately)
      if (isSupported) {
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await (registration as any).pushManager.getSubscription();
          console.log('[Push Debug] Existing subscription:', !!subscription, subscription?.endpoint?.slice(0, 50));
          setState(prev => ({ ...prev, isSubscribed: !!subscription }));
        } catch (error) {
          console.log('[Push] Could not check existing subscription:', error);
        }
      }
    };
    
    checkSupport();
  }, [appUser]);

  const checkExistingSubscription = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await (registration as any).pushManager.getSubscription();
      setState(prev => ({ ...prev, isSubscribed: !!subscription }));
    } catch (error) {
      console.error('[Push] Error checking subscription:', error);
    }
  }, []);

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!state.isSupported || !user || !appUser) {
      console.warn('[Push] Cannot subscribe: not supported or user not logged in');
      return false;
    }

    if (!VAPID_PUBLIC_KEY) {
      console.warn('[Push] VAPID_PUBLIC_KEY not configured');
      setState(prev => ({ ...prev, error: 'Push notifications not configured' }));
      return false;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // 1. Request permission
      const permission = await Notification.requestPermission();
      setState(prev => ({ ...prev, permission }));
      
      if (permission !== 'granted') {
        setState(prev => ({ ...prev, isLoading: false, error: 'Permission denied' }));
        return false;
      }

      // 2. Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // 3. Subscribe to push
      const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      const subscription = await (registration as any).pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey.buffer as ArrayBuffer
      });

      // 4. Save to database
      const keys = subscription.toJSON().keys;
      
      const { error: dbError } = await supabase
        .from('push_subscriptions')
        .upsert({
          app_user_id: appUser.id,
          endpoint: subscription.endpoint,
          p256dh: keys?.p256dh || '',
          auth: keys?.auth || '',
          device_info: {
            userAgent: navigator.userAgent,
            language: navigator.language,
            platform: navigator.platform,
            timestamp: new Date().toISOString()
          },
          is_active: true
        }, { 
          onConflict: 'endpoint',
          ignoreDuplicates: false 
        });

      if (dbError) {
        console.error('[Push] Error saving subscription:', dbError);
        setState(prev => ({ ...prev, isLoading: false, error: 'Failed to save subscription' }));
        return false;
      }

      console.log('[Push] Successfully subscribed to push notifications');
      setState(prev => ({ ...prev, isSubscribed: true, isLoading: false }));
      return true;
    } catch (error) {
      console.error('[Push] Subscription failed:', error);
      setState(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Subscription failed' 
      }));
      return false;
    }
  }, [state.isSupported, user, appUser]);

  const unsubscribe = useCallback(async (): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await (registration as any).pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe from push
        await subscription.unsubscribe();
        
        // Remove from database
        await supabase
          .from('push_subscriptions')
          .delete()
          .eq('endpoint', subscription.endpoint);
      }
      
      console.log('[Push] Successfully unsubscribed');
      setState(prev => ({ ...prev, isSubscribed: false, isLoading: false }));
      return true;
    } catch (error) {
      console.error('[Push] Unsubscribe failed:', error);
      setState(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Unsubscribe failed' 
      }));
      return false;
    }
  }, []);

  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!state.isSupported) return 'denied';
    
    const permission = await Notification.requestPermission();
    setState(prev => ({ ...prev, permission }));
    return permission;
  }, [state.isSupported]);

  const sendTestNotification = useCallback(async (): Promise<{ success: boolean; sent?: number; failed?: number; error?: string }> => {
    if (!appUser) {
      return { success: false, error: 'User not logged in' };
    }

    try {
      const { data, error } = await supabase.functions.invoke('send-push-notification', {
        body: {
          app_user_id: appUser.id,
          title: '🔔 Teste de Notificação',
          body: 'Se você está vendo isso, as push notifications estão funcionando!',
          url: '/profile',
          tag: 'test-notification'
        }
      });

      if (error) {
        console.error('[Push] Test notification failed:', error);
        return { success: false, error: error.message };
      }

      console.log('[Push] Test notification result:', data);
      return { 
        success: data?.sent > 0, 
        sent: data?.sent || 0, 
        failed: data?.failed || 0 
      };
    } catch (error) {
      console.error('[Push] Test notification error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }, [appUser]);

  return {
    ...state,
    subscribe,
    unsubscribe,
    requestPermission,
    checkExistingSubscription,
    sendTestNotification,
  };
}
